import { handleApiError } from '@/store/api/apiErrorHandler';
import {
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,
} from '@/store/api/systemSettingApi';
import { showToastMessage } from '@/utils';
import { useEffect, useState } from 'react';

const NumberInput = ({
  value,
  onChange,
  step = 1,
  min = 0,
  width = `100%`,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  width?: string;
  min?: number;
}) => (
  <div
    className="flex items-stretch rounded-lg border border-green-400 bg-[#F6FFF8] border-green overflow-hidden"
    style={{ width: width }}
  >
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={min}
      className="w-full px-3 py-2 text-sm bg-transparent outline-none no-spinner"
    />

    <div className="flex border-l border-green-400">
      <button
        type="button"
        className="px-3 text-red-500 hover:bg-red-100 transition border border-l-green"
        onClick={() => value - step >= 0 && onChange(value - step)}
      >
        −
      </button>
      <button
        type="button"
        className="px-3 text-green hover:bg-green/10 transition border border-l-green"
        onClick={() => onChange(value + step)}
      >
        +
      </button>
    </div>
  </div>
);

const AffiliateGeneralSetting = () => {
  const { data: systemSettings } = useGetSystemSettingsQuery();
  const [updateSettings] = useUpdateSystemSettingsMutation();

  const [minTurnover, setMinTurnover] = useState(300);
  const [platformDeduction, setPlatformDeduction] = useState(18);
  const [depositCharge, setDepositCharge] = useState(2);
  const [withdrawalCharge, setWithdrawalCharge] = useState(1.5);

  useEffect(() => {
    if (!systemSettings) return;

    const { active_user, platform, deposit, withdrawal } = systemSettings;
    setMinTurnover(Number(active_user.selection ?? active_user.default));
    setPlatformDeduction(Number(platform.selection ?? platform.default));
    setDepositCharge(Number(deposit.selection ?? deposit.default));
    setWithdrawalCharge(Number(withdrawal.selection ?? withdrawal.default));
  }, [systemSettings]);

  const handleSaveTurnoverSetting = () => {
    if (!systemSettings) return;

    try {
      updateSettings({
        data: [
          {
            settingId: systemSettings.active_user.id,
            enable: true,
            selection: minTurnover,
          },
        ],
      });

      showToastMessage('success', 'Turnover setting updated!');
    } catch (err) {
      handleApiError(err, 'Failed to update turnover setting');
    }
  };

  const handleSavePlatformSettings = () => {
    if (!systemSettings) return;
    try {
      updateSettings({
        data: [
          {
            settingId: systemSettings.platform.id,
            enable: true,
            selection: platformDeduction,
          },
          {
            settingId: systemSettings.deposit.id,
            enable: true,
            selection: depositCharge,
          },
          {
            settingId: systemSettings.withdrawal.id,
            enable: true,
            selection: withdrawalCharge,
          },
        ],
      });
      showToastMessage('success', 'Platform setting updated!');
    } catch (err) {
      handleApiError(err, 'Failed to update platform setting');
    }
  };

  return (
    <div className="space-y-6">
      {/* Active User Criteria */}
      <div className="bg-white rounded-xl p-6 border shadow-[0_6px_12px_-6px_rgba(0,0,0,0.50)]">
        <h2 className="font-semibold text-lg">Active User Criteria</h2>
        <p className="text-sm text-gray-500 mb-4">
          Define the minimum requirements for a user to be considered “active” in the affiliate
          program
        </p>

        <label className="text-sm font-medium text-gray-700">Minimum Turnover Amount (₹)</label>

        <div className="mt-2">
          <NumberInput value={minTurnover} step={100} onChange={setMinTurnover} width="50%" />
          <p className="text-xs text-black/60 mt-1">
            Minimum turnover required for a player to be considered active
          </p>
        </div>
        <div className="flex justify-end w-1/2">
          <button
            className="mt-4 bg-green text-white px-4 py-1.5 rounded text-sm"
            onClick={handleSaveTurnoverSetting}
          >
            Save
          </button>
        </div>
      </div>

      {/* Platform Deduction */}
      <div className="bg-white rounded-xl p-6 border  shadow-[0_6px_12px_-6px_rgba(0,0,0,0.50)]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {/* LEFT SIDE */}
          <div className="md:col-span-2 space-y-4">
            <h2 className="font-semibold text-lg">Platform Deduction Structure</h2>
            <p className="text-sm text-gray-500">
              Configure platform deduction percentage and deposit/withdrawal charges
            </p>

            <div>
              <label className="text-sm">Platform Deduction (%)</label>
              <NumberInput value={platformDeduction} onChange={setPlatformDeduction} width="70%" />
              <p className="text-xs text-black/60 pt-1">
                Percentage deducted from affiliate commissions
              </p>
            </div>

            <div>
              <label className="text-sm">Deposit Charged (%)</label>
              <NumberInput value={depositCharge} onChange={setDepositCharge} width="70%" />
              <p className="text-xs text-black/60 pt-1">Percentage charged on deposits</p>
            </div>

            <div>
              <label className="text-sm">Withdrawal (%)</label>
              <NumberInput value={withdrawalCharge} onChange={setWithdrawalCharge} width="70%" />
              <p className="text-xs text-black/60 pt-1">Percentage charged on withdrawals</p>
            </div>

            <div className="flex justify-end w-[70%]">
              <button
                className="mt-4 bg-green text-white px-4 py-1.5 rounded text-sm"
                onClick={handleSavePlatformSettings}
              >
                Save
              </button>
            </div>
          </div>

          {/* RIGHT SIDE – CURRENT STRUCTURE */}
          <div className="md:col-span-1">
            <div className="rounded-t-2xl rounded-b-3xl border border-blue-500 from-blue-400 to-blue-700 sticky top-4 bg-[linear-gradient(to_top,#244DA4_0%,#244DA4_80%,#A4CCF1_100%)]">
              {/* Header */}

              <div className="px-5 py-3 text-black font-semibold">Current Structure:</div>

              {/* Inner Content */}
              <div className="bg-[#FBF7F7] rounded-2xl p-5 text-sm text-gray-700">
                <ul className="space-y-3">
                  <li className="py-2">
                    <span className="text-gray-500 mr-2">01</span>
                    Platform Deduction:{' '}
                    <b className="text-black">
                      {systemSettings?.platform?.selection ||
                        systemSettings?.platform?.default ||
                        'N/A'}
                      %
                    </b>
                  </li>
                  <li className="py-2">
                    <span className="text-gray-500 mr-2">02</span>
                    Deposit Charge:{' '}
                    <b className="text-black">
                      {' '}
                      {systemSettings?.deposit?.selection ||
                        systemSettings?.deposit?.default ||
                        'N/A'}
                      %
                    </b>
                  </li>
                  <li className="py-2">
                    <span className="text-gray-500 mr-2">03</span>
                    Withdrawal Charge:{' '}
                    <b className="text-black">
                      {' '}
                      {systemSettings?.withdrawal?.selection ||
                        systemSettings?.withdrawal?.default ||
                        'N/A'}
                      %
                    </b>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateGeneralSetting;
